import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, PickerList, useFloating } from '@platform/ui';
import type { IMdMenuProps } from './IMdMenuProps';

/**
 * Кнопка-меню панелі форматування (прототип kbColorPk, kbLinkPk): 30px, прозора рамка до наведення, вміст + шеврон; плаваючий список
 * пікера (за потреби з пошуком); вибір пункту виконує дію й закриває меню. mousedown не забирає фокус і виділення з поля статті.
 */
export function MdMenu({ label, content, options, onPick, query, onQuery, loading, emptyText, footer }: IMdMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const listId = useId();
  const close = () => { setOpen(false); onQuery?.(''); };
  const pos = useFloating(open, button, panel, close, { estimate: 260, minWidth: 220, matchWidth: false });
  const pick = (value: string) => { close(); onPick(value); };
  // Фокус пішов з пошуку не в меню (наприклад, у вікно розширеного пошуку) — меню закривається.
  const leave = () => setTimeout(() => {
    const a = document.activeElement;
    if (!a || (!button.current?.contains(a) && !panel.current?.contains(a))) close();
  }, 150);
  return (
    <>
      <button ref={button} type="button" className="md-editor__tool md-editor__tool--menu" title={label} aria-label={label}
        aria-haspopup="listbox" aria-expanded={open} onMouseDown={(e) => e.preventDefault()} onClick={() => { setHi(0); setOpen((o) => !o); }}>
        {content}<Icon name="chevron-down" size={13} strokeWidth={2} />
      </button>
      {open && pos && (
        <PickerList listId={listId} panelRef={panel} pos={pos} label={label} options={options} value="" hi={hi}
          onPick={(o) => pick(o.value)} onHover={setHi} loading={loading} emptyText={emptyText ?? t('fieldControls.noMatches')} footer={footer}
          search={onQuery ? {
            value: query ?? '', onChange: onQuery, onBlur: leave, placeholder: t('fieldControls.search'), label,
            onKeyDown: (e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, options.length - 1)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
              else if (e.key === 'Enter' && options[hi]) { e.preventDefault(); pick(options[hi]!.value); }
            },
          } : null} />
      )}
    </>
  );
}
